import {createContext, ReactNode, useContext, useEffect, useState} from "react";

// Bootstrap colour modes via data-bs-theme on <html>. public/theme-init.js applies the stored mode
// before the first paint; keep its storage key and values in step with these.
export type ThemeMode = 'light' | 'dark' | 'auto';

const STORAGE_KEY = 'tsa-theme';
const darkQuery = () => window.matchMedia('(prefers-color-scheme: dark)');

function storedMode(): ThemeMode {
    try {
        const mode = localStorage.getItem(STORAGE_KEY);
        return mode === 'light' || mode === 'dark' ? mode : 'auto';
    } catch {
        return 'auto';
    }
}

function apply(mode: ThemeMode): void {
    const dark = mode === 'dark' || (mode === 'auto' && darkQuery().matches);
    document.documentElement.setAttribute('data-bs-theme', dark ? 'dark' : 'light');
}

interface ThemeState {
    mode: ThemeMode;
    setMode(mode: ThemeMode): void;
}

const ThemeContext = createContext<ThemeState | null>(null);

export function ThemeProvider({children}: { children: ReactNode }) {
    const [mode, setModeState] = useState<ThemeMode>(storedMode);

    useEffect(() => {
        apply(mode);
        if (mode !== 'auto') {
            return;
        }
        // Follow the OS setting while it changes.
        const query = darkQuery();
        const onChange = () => apply('auto');
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, [mode]);

    const setMode = (next: ThemeMode) => {
        try {
            if (next === 'auto') {
                localStorage.removeItem(STORAGE_KEY);
            } else {
                localStorage.setItem(STORAGE_KEY, next);
            }
        } catch {
            // Not remembered, but still applied.
        }
        setModeState(next);
    };

    return <ThemeContext.Provider value={{mode, setMode}}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
    const state = useContext(ThemeContext);
    if (!state) {
        throw new Error('useTheme needs a ThemeProvider');
    }
    return state;
}
